import React, { useState, useEffect, useRef, useMemo } from 'react';
import {
  Box,
  Typography,
  Chip,
  Button,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Autocomplete,
  CircularProgress,
  Divider,
} from '@mui/material';
import axios from 'axios';
import { ep } from '../api/endpoints';
import * as Icons from '../constants/icons';
import { STANDARD_CTA_BUTTON_SX, STANDARD_TEXT_BUTTON_SX } from '../constants/buttonStyles';
import { useConfirm } from '../providers/ConfirmProvider';
import { useMasterData } from '../providers/MasterDataProvider';
import { toKatakana } from '../utils/validation';
import CrmCustomerForm from './CrmCustomerForm';
import { EventParticipantRow, EventParticipantInput } from '../hooks/useEventManagement';

/**
 * イベント参加者の編集・お連れ様の追加ダイアログ。
 *
 * - 氏名・フリガナ・役職・メールを直す (申込サイトの参加者欄と同じ項目)
 * - 顧客管理 (CRM) の個人顧客と紐付ける。氏名・メールが一致する候補をサーバーで探し、
 *   見つからない人はその場で個人顧客を新規登録して紐付ける (勤務先は申込の会社の法人顧客を初期値にする)
 * - お連れ様の追加は CRM を軸にする方針のため、個人顧客の紐付けを必須にする
 */

// 紐付ける個人顧客 (候補・検索・新規登録のいずれかで選んだもの)
type LinkedCustomer = { customer_id: string; display_name: string; employer_name?: string | null };

// サーバーの候補 (find_event_participant_customers)
type CustomerCandidate = {
  customer_id: string;
  display_name: string;
  display_kana: string | null;
  occupation: string | null;
  employer_name: string | null;
  email: string | null;
  name_match: boolean;
  email_match: boolean;
  employer_match: boolean;
  linked_reception_no: string | null;
  linked_name: string | null;
};

type EmployerCandidate = { id: string; company_name: string; customer_branch_name: string | null };

// 手動検索 (search_crm_customers) の1件
type SearchOption = { id: string; customer_type: string; display_name: string; employer_name?: string | null };

// 追加先・編集対象の申込 (受付NO・会社名・当社担当)
export type EventParticipantEntry = {
  entry_id: number;
  reception_no: string;
  company_name: string;
  hozenall_user_id: string | null;
};

type FormState = {
  last_name: string;
  first_name: string;
  last_kana: string;
  first_kana: string;
  position: string;
  email: string;
};

const EMPTY_FORM: FormState = { last_name: '', first_name: '', last_kana: '', first_kana: '', position: '', email: '' };

const matchChip = (label: string, color: string, bg: string) => (
  <Chip size="small" label={label} sx={{ fontSize: '10px', height: '18px', fontWeight: 700, bgcolor: bg, color }} />
);

const EventParticipantDialog = React.memo(function EventParticipantDialog({
  open,
  mode,
  entry,
  participant,
  eventName,
  participants,
  onClose,
  onSave,
}: {
  open: boolean;
  mode: 'edit' | 'add';
  entry: EventParticipantEntry;
  // 編集対象 (mode=edit のみ)
  participant?: EventParticipantRow | null;
  eventName: string;
  // メールアドレスの重複確認に使う同じイベントの参加者
  participants: EventParticipantRow[];
  onClose: () => void;
  onSave: (input: EventParticipantInput) => Promise<boolean>;
}) {
  const confirm = useConfirm();
  const { staffList } = useMasterData();
  const [form, setForm] = useState<FormState>(EMPTY_FORM);
  const [linked, setLinked] = useState<LinkedCustomer | null>(null);
  const [candidates, setCandidates] = useState<CustomerCandidate[]>([]);
  const [employers, setEmployers] = useState<EmployerCandidate[]>([]);
  const [finding, setFinding] = useState(false);
  const [searchOptions, setSearchOptions] = useState<SearchOption[]>([]);
  const [searching, setSearching] = useState(false);
  const [registerOpen, setRegisterOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const findTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const findSeqRef = useRef(0);
  const searchTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const searchSeqRef = useRef(0);

  // 開いたときに対象の参加者 (追加は空欄) で初期化する
  useEffect(() => {
    if (!open) return;
    if (mode === 'edit' && participant) {
      setForm({
        last_name: participant.last_name ?? '',
        first_name: participant.first_name ?? '',
        last_kana: participant.last_kana ?? '',
        first_kana: participant.first_kana ?? '',
        position: participant.position ?? '',
        email: participant.email ?? '',
      });
      setLinked(
        participant.customer_id
          ? {
              customer_id: participant.customer_id,
              display_name: participant.customer_name ?? '(氏名なし)',
              employer_name: participant.customer_employer_name,
            }
          : null,
      );
    } else {
      setForm(EMPTY_FORM);
      setLinked(null);
    }
    setCandidates([]);
    setEmployers([]);
    setSearchOptions([]);
  }, [open, mode, participant]);

  // 氏名・メールが変わったら紐付けの候補を探し直す (入力中の連続呼び出しを避けるため 500ms 待つ)
  useEffect(() => {
    if (!open) return;
    if (findTimerRef.current) clearTimeout(findTimerRef.current);
    const hasKey = (form.last_name.trim() && form.first_name.trim()) || form.email.includes('@');
    if (!hasKey) {
      setCandidates([]);
      return;
    }
    const seq = ++findSeqRef.current;
    setFinding(true);
    findTimerRef.current = setTimeout(async () => {
      try {
        const res = await axios.post(ep('find_event_participant_customers'), {
          entry_id: entry.entry_id,
          last_name: form.last_name,
          first_name: form.first_name,
          email: form.email,
          participant_id: participant?.id,
        });
        if (seq !== findSeqRef.current) return;
        if (res.data.status === 'success') {
          setCandidates(res.data.data?.customers || []);
          setEmployers(res.data.data?.employers || []);
        }
      } catch {
        if (seq === findSeqRef.current) setCandidates([]);
      } finally {
        if (seq === findSeqRef.current) setFinding(false);
      }
    }, 500);
    return () => {
      if (findTimerRef.current) clearTimeout(findTimerRef.current);
    };
  }, [open, entry.entry_id, participant?.id, form.last_name, form.first_name, form.email]);

  const handleChange = (field: keyof FormState, value: string) => setForm((prev) => ({ ...prev, [field]: value }));

  // フリガナはひらがな・半角カナを全角カナへ寄せる (顧客管理の入力と同じ)
  const handleKanaBlur = (field: 'last_kana' | 'first_kana') => {
    const converted = toKatakana(form[field]);
    if (converted !== form[field]) handleChange(field, converted);
  };

  // 顧客管理の個人顧客を氏名で手動検索する (候補に出ない人の紐付け用)
  const handleSearch = (query: string) => {
    if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
    if (!query.trim()) {
      setSearchOptions([]);
      return;
    }
    setSearching(true);
    const seq = ++searchSeqRef.current;
    searchTimerRef.current = setTimeout(async () => {
      try {
        const res = await axios.post(ep('search_crm_customers'), { query });
        if (seq !== searchSeqRef.current) return;
        const rows: SearchOption[] = res.data.status === 'success' ? res.data.results || [] : [];
        setSearchOptions(rows.filter((r) => r.customer_type === 'individual'));
      } catch {
        if (seq === searchSeqRef.current) setSearchOptions([]);
      } finally {
        if (seq === searchSeqRef.current) setSearching(false);
      }
    }, 300);
  };

  // 新規登録フォームの初期値 (参加者の入力 + 申込の会社の法人顧客 + 申込の当社担当)
  const registerInitialValues = useMemo(() => {
    const staff = entry.hozenall_user_id ? staffList.find((s: any) => s.staff_id === entry.hozenall_user_id) : null;
    const employer = employers.length === 1 ? employers[0] : null;
    const email = form.email.trim();
    return {
      last_name: form.last_name.trim(),
      first_name: form.first_name.trim(),
      last_name_kana: form.last_kana.trim(),
      first_name_kana: form.first_kana.trim(),
      occupation: form.position.trim(),
      contacts: email ? [{ scope: 'business', contact_kind: 'email', value: email, is_primary: true, sort_no: 0 }] : [],
      registration_source: 'event_participant',
      remarks: `${eventName} の参加者として登録`,
      ...(employer
        ? {
            employer_id: employer.id,
            employer_name: employer.company_name + (employer.customer_branch_name ? ` ${employer.customer_branch_name}` : ''),
          }
        : {}),
      ...(staff ? { our_staff_id: staff.staff_id, our_staff_branch: staff.branch || '' } : {}),
    };
  }, [form, employers, entry.hozenall_user_id, staffList, eventName]);

  const handleSave = async () => {
    if (saving) return;
    const email = form.email.trim();
    // 申込サイトはメールアドレスで二重申込を確かめているため、同じイベントで重なる場合は確認する
    if (email) {
      const dup = participants.find(
        (p) => p.id !== participant?.id && (p.email || '').trim().toLowerCase() === email.toLowerCase(),
      );
      if (dup) {
        const ok = await confirm({
          title: 'メールアドレスの重複',
          message: `受付NO ${dup.reception_no} の ${dup.last_name ?? ''} ${dup.first_name ?? ''} 様と同じメールアドレスです。このまま保存しますか？`,
          confirmText: '保存する',
        });
        if (!ok) return;
      }
    }
    const input: EventParticipantInput = {
      last_name: form.last_name.trim() || null,
      first_name: form.first_name.trim() || null,
      last_kana: form.last_kana.trim() || null,
      first_kana: form.first_kana.trim() || null,
      position: form.position.trim() || null,
      email: email || null,
      customer_id: linked?.customer_id ?? null,
    };
    setSaving(true);
    const ok = await onSave(input);
    setSaving(false);
    if (ok) onClose();
  };

  const canSave = !!form.last_name.trim() && (mode === 'edit' || !!linked) && !saving;
  const fieldSx = { '& .MuiInputBase-input': { fontSize: '13px' }, '& .MuiInputLabel-root': { fontSize: '13px' } };

  return (
    <>
      <Dialog open={open} onClose={() => !saving && onClose()} maxWidth="sm" fullWidth disableScrollLock>
        <DialogTitle sx={{ fontSize: '0.875rem', fontWeight: 700, py: 0.75, display: 'flex', alignItems: 'center', gap: 1 }}>
          {mode === 'edit' ? <Icons.Edit sx={{ fontSize: 18 }} /> : <Icons.PersonAdd sx={{ fontSize: 18 }} />}
          {mode === 'edit' ? '参加者の編集' : 'お連れ様の追加'}
          <Typography component="span" sx={{ fontSize: '12px', color: '#64748b', fontWeight: 400, ml: 1 }}>
            受付NO {entry.reception_no}　{entry.company_name}
          </Typography>
        </DialogTitle>
        <DialogContent sx={{ pt: '12px !important' }}>
          <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1.5 }}>
            <TextField size="small" label="姓" required value={form.last_name} onChange={(e) => handleChange('last_name', e.target.value)} sx={fieldSx} autoFocus={mode === 'add'} inputProps={{ maxLength: 50 }} />
            <TextField size="small" label="名" value={form.first_name} onChange={(e) => handleChange('first_name', e.target.value)} sx={fieldSx} inputProps={{ maxLength: 50 }} />
            <TextField size="small" label="セイ" value={form.last_kana} onChange={(e) => handleChange('last_kana', e.target.value)} onBlur={() => handleKanaBlur('last_kana')} sx={fieldSx} inputProps={{ maxLength: 50 }} />
            <TextField size="small" label="メイ" value={form.first_kana} onChange={(e) => handleChange('first_kana', e.target.value)} onBlur={() => handleKanaBlur('first_kana')} sx={fieldSx} inputProps={{ maxLength: 50 }} />
            <TextField size="small" label="役職" value={form.position} onChange={(e) => handleChange('position', e.target.value)} sx={fieldSx} inputProps={{ maxLength: 50 }} />
            <TextField size="small" label="メールアドレス" value={form.email} onChange={(e) => handleChange('email', e.target.value)} sx={fieldSx} inputProps={{ maxLength: 150 }} />
          </Box>

          <Divider sx={{ my: 2 }} />

          <Typography sx={{ fontSize: '13px', fontWeight: 700, mb: 1, display: 'flex', alignItems: 'center', gap: 0.75 }}>
            <Icons.Link sx={{ fontSize: 18, color: '#64748b' }} /> 顧客管理との紐付け
            {mode === 'add' && <Typography component="span" sx={{ fontSize: '11px', color: '#b91c1c' }}>（追加には必須）</Typography>}
          </Typography>

          {linked ? (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, p: 1, borderRadius: 1, bgcolor: '#ecfdf5', border: '1px solid #a7f3d0' }}>
              <Icons.Check sx={{ fontSize: 16, color: '#059669' }} />
              <Typography sx={{ fontSize: '13px', fontWeight: 700 }}>{linked.display_name}</Typography>
              {linked.employer_name && <Typography sx={{ fontSize: '12px', color: '#64748b' }}>（{linked.employer_name}）</Typography>}
              <Box sx={{ flex: 1 }} />
              <Button size="small" startIcon={<Icons.Close sx={{ fontSize: 14 }} />} onClick={() => setLinked(null)} sx={{ ...STANDARD_TEXT_BUTTON_SX, fontSize: '12px' }}>
                紐付けを外す
              </Button>
            </Box>
          ) : (
            <>
              <Typography sx={{ fontSize: '12px', color: '#64748b', mb: 1 }}>
                氏名・メールアドレスが一致する個人顧客の候補です。該当する人がいなければ、検索するか新規登録してください。
              </Typography>
              {finding ? (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 1 }}>
                  <CircularProgress size={14} />
                  <Typography sx={{ fontSize: '12px', color: '#94a3b8' }}>候補を探しています…</Typography>
                </Box>
              ) : candidates.length === 0 ? (
                <Typography sx={{ fontSize: '12px', color: '#94a3b8', py: 0.5 }}>一致する個人顧客はいません</Typography>
              ) : (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75, maxHeight: 220, overflowY: 'auto', mb: 1 }}>
                  {candidates.map((c) => (
                    <Box
                      key={c.customer_id}
                      sx={{ display: 'flex', alignItems: 'center', gap: 1, p: 0.75, borderRadius: 1, border: '1px solid #e2e8f0' }}
                    >
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap' }}>
                          <Typography sx={{ fontSize: '13px', fontWeight: 700 }}>{c.display_name}</Typography>
                          {c.employer_name && <Typography sx={{ fontSize: '12px', color: '#64748b' }}>（{c.employer_name}）</Typography>}
                          {c.employer_match && matchChip('勤務先一致', '#059669', '#ecfdf5')}
                          {c.email_match && matchChip('メール一致', '#3b82f6', '#eff6ff')}
                          {c.name_match && matchChip('氏名一致', '#64748b', '#f1f5f9')}
                        </Box>
                        <Typography sx={{ fontSize: '11px', color: '#94a3b8', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {[c.occupation, c.email].filter(Boolean).join(' / ')}
                        </Typography>
                      </Box>
                      {c.linked_reception_no ? (
                        <Typography sx={{ fontSize: '11px', color: '#b91c1c', whiteSpace: 'nowrap' }}>
                          受付NO {c.linked_reception_no} {c.linked_name} 様に紐付け済み
                        </Typography>
                      ) : (
                        <Button
                          size="small"
                          onClick={() => setLinked({ customer_id: c.customer_id, display_name: c.display_name, employer_name: c.employer_name })}
                          sx={{ ...STANDARD_TEXT_BUTTON_SX, fontSize: '12px', whiteSpace: 'nowrap' }}
                        >
                          紐付ける
                        </Button>
                      )}
                    </Box>
                  ))}
                </Box>
              )}
              <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', mt: 1 }}>
                <Autocomplete
                  sx={{ flex: 1 }}
                  options={searchOptions}
                  loading={searching}
                  value={null}
                  filterOptions={(x) => x}
                  getOptionLabel={(o) => (o.employer_name ? `${o.display_name}（${o.employer_name}）` : o.display_name || '')}
                  isOptionEqualToValue={(o, v) => o.id === v.id}
                  noOptionsText="氏名で検索してください"
                  onInputChange={(_, v, reason) => {
                    if (reason === 'input') handleSearch(v);
                  }}
                  onChange={(_, v) => {
                    if (v) setLinked({ customer_id: v.id, display_name: v.display_name, employer_name: v.employer_name });
                  }}
                  renderOption={(props, o) => (
                    <li {...props} key={o.id}>
                      <Typography sx={{ fontSize: '13px' }}>{o.display_name}</Typography>
                      {o.employer_name && <Typography sx={{ fontSize: '12px', color: '#94a3b8', ml: 0.75 }}>（{o.employer_name}）</Typography>}
                    </li>
                  )}
                  renderInput={(params) => <TextField {...params} size="small" placeholder="個人顧客を氏名で検索" sx={fieldSx} />}
                />
                <Button
                  size="small"
                  startIcon={<Icons.PersonAdd sx={{ fontSize: 16 }} />}
                  onClick={() => setRegisterOpen(true)}
                  disabled={!form.last_name.trim()}
                  sx={{ ...STANDARD_TEXT_BUTTON_SX, fontSize: '12px', whiteSpace: 'nowrap' }}
                >
                  新規登録して紐付け
                </Button>
              </Box>
            </>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={onClose} disabled={saving} sx={STANDARD_TEXT_BUTTON_SX}>
            キャンセル
          </Button>
          <Button
            onClick={handleSave}
            disabled={!canSave}
            startIcon={saving ? <CircularProgress size={14} /> : mode === 'edit' ? <Icons.Save /> : <Icons.PersonAdd />}
            sx={STANDARD_CTA_BUTTON_SX}
          >
            {mode === 'edit' ? '保存する' : '追加する'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* 顧客管理への新規登録 (共通フォーム)。重複候補から既存顧客を選んだ場合もその顧客を紐付ける */}
      {registerOpen && (
        <CrmCustomerForm
          open
          initialCustomerType="individual"
          initialValues={registerInitialValues}
          onClose={() => setRegisterOpen(false)}
          onSuccess={(customerId, displayName) => {
            setRegisterOpen(false);
            // 勤務先はフォームで変えられるため表示しない (保存後の一覧の再取得で正しい勤務先が出る)
            setLinked({
              customer_id: customerId,
              display_name: displayName || `${form.last_name} ${form.first_name}`.trim(),
              employer_name: null,
            });
          }}
        />
      )}
    </>
  );
});

export default EventParticipantDialog;
